import { merge } from 'lodash';
import { EMPLOYEE_TYPES_ALIAS } from 'domain/shared/employee';
import type { PositionType } from 'domain/shared/employee';
import type { Technology } from 'domain/shared/career';
import { POSITION_SLOT_WORKER_TYPE } from 'domain/people/positionSlots/constants';
import type { PositionSlotWorkerType } from 'domain/people/positionSlots/constants';
import type { QualificationCompetencyLevel } from 'domain/shared/performanceMeasurement';

export type CandidateHiringContextVacancy = {
  id: string;
  specialization: string;
  technology: Nullable<string>;
  competencyLevel: QualificationCompetencyLevel;
  slotWorkerType: Nullable<PositionSlotWorkerType>;
};

export type CandidateHiringContextResponse = {
  vacancy?: Nullable<CandidateHiringContextVacancy>;
};

export type ConversionPositionSlotFormData = {
  positionSlotId: string;
  employeeType: Nullable<string>;
  positionId: number;
  technologyId: Nullable<string>;
  competencyLevel: QualificationCompetencyLevel;
};

const EMPLOYEE_TYPE_BY_SLOT_WORKER_TYPE: Partial<Record<PositionSlotWorkerType, string>> = {
  [POSITION_SLOT_WORKER_TYPE.STAFF]: EMPLOYEE_TYPES_ALIAS.EMPLOYEE,
  [POSITION_SLOT_WORKER_TYPE.CONTRACTOR]: EMPLOYEE_TYPES_ALIAS.CONTRACTOR,
};

const resolveEmployeeType = (workerType: Nullable<PositionSlotWorkerType>): Nullable<string> =>
  (workerType && EMPLOYEE_TYPE_BY_SLOT_WORKER_TYPE[workerType]) || null;

const findPositionBySpecialization = (positions: PositionType[], specialization: string) =>
  positions.find(position => position.name === specialization || position.alias === specialization);

const findTechnologyByName = (technologies: Technology[], technology: string) =>
  technologies.find(item => item.id === technology || item.name === technology);

export const mapConversionPositionSlotToFormData = (
  vacancy: CandidateHiringContextVacancy,
  positions: PositionType[],
  technologies: Technology[],
): Nullable<ConversionPositionSlotFormData> => {
  const position = findPositionBySpecialization(positions, vacancy.specialization);

  if (!position?.id) {
    return null;
  }

  const technology = vacancy.technology ? findTechnologyByName(technologies, vacancy.technology) : null;

  return {
    positionSlotId: vacancy.id,
    employeeType: resolveEmployeeType(vacancy.slotWorkerType),
    positionId: position.id,
    technologyId: technology?.id ?? null,
    competencyLevel: vacancy.competencyLevel,
  };
};

const mapPositionSlotToInitialData = (positionSlot: ConversionPositionSlotFormData) => ({
  employeeType: positionSlot.employeeType ?? '',
  positionId: String(positionSlot.positionId),
  specializationInfo: {
    technologyId: positionSlot.technologyId,
    competencyLevel: positionSlot.competencyLevel,
  },
});

export const applyPositionSlotToInitialData = <T extends object>(
  initialData: T,
  positionSlot: Nullable<ConversionPositionSlotFormData>,
): T => (positionSlot ? merge({}, initialData, mapPositionSlotToInitialData(positionSlot)) : initialData);
